import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames/bind';
import { useAppSelector } from '../../../store/hooks';
import { selectUserInformation } from '../../../store/reducers';
import type { ReducerAction } from '../../../types/navigationIndex.types';
import { Header } from '../../organisms/Header/Header';
import { ConfirmationStep } from './AppointmentSteps/ConfirmationStep';
import { DateStep } from './AppointmentSteps/DateStep';
import { OfflineLocationStep } from './AppointmentSteps/LocationStep';
import { ProductStep } from './AppointmentSteps/ProductStep';
import { TimeStep } from './AppointmentSteps/TimeStep';
import style from './Appointment.module.scss';

const cx = classNames.bind(style);

type AppointmentProps = {
  isFirstAppointment: boolean;
  isOffline: boolean;
  currentPage: number;
  setCurrentPage: React.Dispatch<ReducerAction>;
};

const onlineFirstAppointmentClasses = cx({ OnlineFirstAppointment: true });

export function Appointment({ isFirstAppointment, isOffline, currentPage, setCurrentPage }: AppointmentProps) {
  const userInformation = useAppSelector(selectUserInformation);
  const navigate = useNavigate();
  const [appointmentInfo, setAppointmentInfo] = useState(() => {
    if (isFirstAppointment) {
      return { firstAppointmentDuration: 20 };
    }

    return {};
  });

  useEffect(() => {
    const actionToDispatch = {
      type: '[UPDATE]',
      payload: { appointmentIndex: isOffline && isFirstAppointment ? 0 : 1 },
    };
    setCurrentPage(actionToDispatch);
  }, [isFirstAppointment, isOffline, setCurrentPage]);

  useEffect(() => {
    if (!isFirstAppointment && userInformation?.requiresFirstAppointment) {
      navigate('/profile/appointment-choice', { replace: true, state: { requiresFirstAppointment: true } });
    }
  }, [isFirstAppointment, navigate, userInformation]);

  const initialPageForCurrentFlow = isOffline && isFirstAppointment ? 0 : 1;
  if (appointmentInfo && Object.entries(appointmentInfo).length === 0 && currentPage !== initialPageForCurrentFlow) {
    return null;
  }

  let pageToRender;
  switch (currentPage) {
    // As OfflineLocationStep is optional, currentPage doesn't necessarily reflect ProgressStep numbers in the UI.
    case 0:
      pageToRender = (
        <OfflineLocationStep
          appointmentInfo={appointmentInfo}
          setAppointmentInfo={setAppointmentInfo}
          setCurrentPage={setCurrentPage}
        />
      );
      break;
    // Most appointments will start with step 1, only first offline appointments will start with step 0.
    case 1:
      pageToRender = (
        <DateStep
          isFirstAppointment={isFirstAppointment}
          isOffline={isOffline}
          appointmentInfo={appointmentInfo}
          setAppointmentInfo={setAppointmentInfo}
          setCurrentPage={setCurrentPage}
        />
      );
      break;
    case 2:
      pageToRender = (
        <TimeStep
          isFirstAppointment={isFirstAppointment}
          isOffline={isOffline}
          appointmentInfo={appointmentInfo}
          setAppointmentInfo={setAppointmentInfo}
          setCurrentPage={setCurrentPage}
        />
      );
      break;
    case 3:
      pageToRender = (
        <ProductStep
          isFirstAppointment={isFirstAppointment}
          isOffline={isOffline}
          appointmentInfo={appointmentInfo}
          setAppointmentInfo={setAppointmentInfo}
          setCurrentPage={setCurrentPage}
        />
      );
      break;
    case 4:
      pageToRender = (
        <ConfirmationStep
          isOffline={isOffline}
          isFirstAppointment={isFirstAppointment}
          appointmentInfo={appointmentInfo}
        />
      );
      break;
    default:
      pageToRender = <></>;
  }

  let barTitleText = isFirstAppointment ? 'Erstgespräch buchen' : 'Folgetermin buchen';
  if (isOffline && currentPage === 0) {
    barTitleText = 'Standort auswählen';
  }

  return (
    <React.Fragment>
      <Header showBackButton={true} barTitle={barTitleText} />
      <div className={onlineFirstAppointmentClasses}>{pageToRender}</div>
    </React.Fragment>
  );
}
